import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLibraryOrg } from '@/context/LibraryOrgContext';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BookOpen, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookCover = ({ title, imageUrl }) => (
  imageUrl ? (
    <div className="aspect-[3/4] w-full rounded-xl overflow-hidden bg-slate-100">
      <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
    </div>
  ) : (
    <div className="aspect-[3/4] w-full rounded-xl bg-gradient-to-br from-teal-600 to-teal-800 flex items-center justify-center p-3">
      <span className="text-white text-center text-sm font-semibold leading-snug line-clamp-4">{title}</span>
    </div>
  )
);

const BookCard = ({ book, orgSlug }) => (
  <div className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow p-4 flex flex-col">
    <Link to={`/${orgSlug}/bibliotheek/boek/${book.slug}`}>
      <BookCover title={book.title} imageUrl={book.cover_image_url} />
    </Link>
    <div className="mt-3 flex-1">
      <h3 className="font-semibold text-slate-900 line-clamp-2">{book.title}</h3>
      {book.author_name && <p className="text-sm text-slate-500 mt-0.5">{book.author_name}</p>}
      <div className="flex flex-wrap gap-1.5 mt-2">
        {book.category_name && (
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{book.category_name}</span>
        )}
        {book.is_digital ? (
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">Digitaal e-book</span>
        ) : (
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${book.available ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
            {book.available ? 'Beschikbaar' : 'Uitgeleend'}
          </span>
        )}
      </div>
      {book.description && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{book.description}</p>}
    </div>
    <div className="flex gap-2 mt-3">
      <Link
        to={`/${orgSlug}/bibliotheek/boek/${book.slug}`}
        className="flex-1 text-center text-sm font-semibold px-3 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50"
      >
        Bekijk boek
      </Link>
      <Link
        to={`/${orgSlug}/bibliotheek/boek/${book.slug}${book.is_digital ? '' : '?actie=reserveren'}`}
        className="flex-1 text-center text-sm font-semibold px-3 py-2 rounded-xl bg-teal-600 text-white hover:bg-teal-700"
      >
        {book.is_digital ? 'Download' : (book.available ? 'Leen direct' : 'Reserveer')}
      </Link>
    </div>
  </div>
);

const LibraryPublicHomePage = () => {
  const org = useLibraryOrg();
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [notEnabled, setNotEnabled] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/library/${org.id}/settings`)
      .then((res) => setSettings(res.data))
      .catch(() => setNotEnabled(true));
  }, [org.id]);

  useEffect(() => {
    if (!settings?.enabled) return;
    axios.get(`${API}/public/library/${org.id}/categories`).then((res) => setCategories(res.data)).catch(() => {});
  }, [org.id, settings]);

  useEffect(() => {
    if (!settings?.enabled) { setLoading(false); return; }
    setLoading(true);
    const params = {};
    if (search.trim()) params.q = search.trim();
    if (categoryFilter !== 'all') params.category_id = categoryFilter;
    axios.get(`${API}/public/library/${org.id}/books`, { params })
      .then((res) => setBooks(res.data))
      .catch(() => setNotEnabled(true))
      .finally(() => setLoading(false));
  }, [org.id, settings, search, categoryFilter]);

  const heading = useMemo(() => `Bibliotheek van ${org.name}`, [org.name]);

  if (notEnabled || (settings && !settings.enabled)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-center p-6">
        <div>
          <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Deze bibliotheek is momenteel niet publiek beschikbaar.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-5 flex items-center gap-3">
          {org.logoUrl ? (
            <img src={org.logoUrl} alt={org.name} className="h-9 w-9 rounded-full object-cover" />
          ) : (
            <div className="h-9 w-9 rounded-full bg-teal-600 flex items-center justify-center text-white"><BookOpen className="w-5 h-5" /></div>
          )}
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Boekentafel &amp; bibliotheek</p>
            <h1 className="text-lg font-bold text-slate-900">{heading}</h1>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <div className="grid sm:grid-cols-[1fr_auto] gap-3">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Zoek op titel, auteur of onderwerp"
              className="rounded-xl pl-9"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="rounded-xl sm:w-56"><SelectValue placeholder="Alle categorieën" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle categorieën</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {settings?.contact_note && (
          <p className="text-sm text-slate-500 bg-white rounded-xl border border-slate-100 p-3">{settings.contact_note}</p>
        )}

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <div key={i} className="h-72 bg-white rounded-2xl animate-pulse"></div>)}
          </div>
        ) : books.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Geen boeken gevonden.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {books.map((book) => <BookCard key={book.id} book={book} orgSlug={org.slug} />)}
          </div>
        )}
      </main>
    </div>
  );
};

export default LibraryPublicHomePage;
